/**
 * Per-route SEO for the SPA.
 *
 * Googlebot executes JS, so updating <title>/<meta>/<link rel=canonical>/OG on
 * route change makes each public page properly indexable with its own title,
 * description and social card. Social crawlers (FB/WhatsApp/LinkedIn) do NOT run
 * JS — for those, per-tour cards are injected server-side (see API). This hook
 * covers the Google side and the in-app browser preview.
 *
 * Tags are upserted (created if missing, updated otherwise) and tagged with a
 * data-seo attribute so they're easy to reason about. Pages that don't call
 * useSeo() keep the static homepage defaults from index.html.
 */
import { useEffect } from 'react';

const SITE = 'my360';
const ORIGIN = 'https://my360.site';

export interface SeoInput {
  /** Page title (without the site suffix — added automatically). */
  title: string;
  description: string;
  /** Absolute image URL for OG/Twitter. Defaults to the site OG image. */
  image?: string;
  /** Path (e.g. "/pricing" or "/p/abc") → canonical + og:url. Defaults to current path. */
  canonicalPath?: string;
  /** og:type — "website" (default) or "article". */
  type?: 'website' | 'article';
  /** Keep this page out of the index (overrides the default index,follow). */
  noindex?: boolean;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute('data-seo', '');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function useSeo(input: SeoInput): void {
  const { title, description, image, canonicalPath, type = 'website', noindex = false } = input;

  useEffect(() => {
    const fullTitle = title.includes(SITE) ? title : `${title} | ${SITE}`;
    const origin = typeof window !== 'undefined' ? window.location.origin : ORIGIN;
    const path = canonicalPath ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
    const url = path.startsWith('http') ? path : `${origin}${path}`;
    const img = image || `${ORIGIN}/og-image.png`;

    document.title = fullTitle;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');
    upsertCanonical(url);

    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', img);
    upsertMeta('property', 'og:type', type);

    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', img);
  }, [title, description, image, canonicalPath, type, noindex]);
}
